/* Where a deck lives — the data layer behind "My Decks" (#48).
 *
 * One API over two backends, chosen by whether anyone is signed in:
 *
 *   signed out → `localStorage`, exactly today's behaviour
 *   signed in  → the `decks` table from #46, with Row Level Security doing the
 *                isolating rather than anything in this file
 *
 * Deliberately UI-free. The frontend is being redesigned, and a deck-switcher
 * built now would be redrawn; this half is the part that survives, and it is
 * the piece #38 and #20 both point at as the proper fix superseding #12's
 * guard-rail. `src/main.js` still calls `saveDecks`/`loadDecks` directly today
 * — wiring it up is the UI half of #48 and belongs with the redesign.
 *
 * ---------------------------------------------------------------------------
 * Two things this module refuses to do, both of them on purpose:
 *
 * **It never throws.** Every function returns `{ ok, ... }` — the same shape
 * `addCard` uses for a refusal and `auth.js` uses for a failed sign-in. The
 * account is additive: losing the network must cost you the sync, not the app.
 *
 * **It never gates anything behind an account.** Saving a deck costs a row.
 * The supporter tier is about server-side simulation CPU, not about storage or
 * card data — see "Product direction" in CLAUDE.md. Do not add a deck cap here.
 */
import { supabase } from './supabase.js';
import { isSignedIn, currentUser } from './auth.js';
import { emptyDeck, saveDecks, loadDecks } from './deck.js';

/* Mirrors `decks_name_length` in the migration. Enforced here so an over-long
 * name is a refusal the UI can render, not a 400 from Postgres. */
export const NAME_MAX = 120;

/* Mirrors `is_deck_cards`: 50 cards can be at most 50 distinct ids, and the
 * slack is for the over-full work-in-progress lists the builder allows. */
const MAX_DISTINCT_IDS = 60;

const ok = extra => ({ ok: true, ...extra });
const fail = error => ({ ok: false, error });

/** The columns "My Decks" needs. Never `select('*')` — see `toDeck`. */
const COLUMNS = 'id, name, leader, cards, created_at, updated_at';

/**
 * A row as the rest of the app wants it: `emptyDeck()`'s shape plus an id.
 *
 * Explicit rather than spread, so a column added to the table later cannot
 * silently start flowing into deck objects that `validate` and `encodeDeck`
 * would then have to tolerate.
 */
const toDeck = row => ({
  id: row.id,
  name: row.name ?? '',
  leader: row.leader ?? null,
  cards: row.cards ?? {},
  updatedAt: row.updated_at ?? null,
});

/** The columns a write may set. `user_id` is defaulted by the table, not us. */
const toRow = deck => ({
  name: String(deck.name ?? '').slice(0, NAME_MAX),
  leader: deck.leader ?? null,
  cards: deck.cards ?? {},
});

/**
 * Is this deck storable at all?
 *
 * Checks the table's *structural* constraints, not deck legality. An illegal
 * deck is the normal state of one you are still building and must always be
 * saveable; a deck that violates `is_deck_cards` would be rejected by Postgres
 * with an error nobody can act on, so it is caught here where the message can
 * name the problem.
 */
export function storeProblems(deck) {
  const out = [];
  if (!deck || typeof deck !== 'object') return ['That is not a deck.'];
  if (String(deck.name ?? '').length > NAME_MAX) {
    out.push(`A deck name can be at most ${NAME_MAX} characters.`);
  }
  const cards = deck.cards;
  if (!cards || typeof cards !== 'object' || Array.isArray(cards)) {
    out.push('That deck has no card list.');
    return out;
  }
  const ids = Object.keys(cards);
  if (ids.length > MAX_DISTINCT_IDS) {
    out.push(`A deck can hold at most ${MAX_DISTINCT_IDS} different cards.`);
  }
  for (const [id, n] of Object.entries(cards)) {
    if (!Number.isInteger(n) || n < 1 || n > 99) {
      out.push(`${id} has a count of ${n}, which cannot be stored.`);
      break;   // one example is enough to act on; the rest are the same bug
    }
  }
  return out;
}

/* ---------------------------------------------------------------------- */
/* which deck you are editing                                              */
/* ---------------------------------------------------------------------- */

/**
 * The active deck is a *per-device* preference, not account state.
 *
 * So it lives in localStorage under both backends, and signing in on your
 * phone does not move the deck open on your laptop. It is one string — a
 * `local:N` index signed out, a uuid signed in — rather than two mechanisms,
 * because the caller should not have to know which backend answered.
 *
 * `loadDecks().activeId` is still the local array's own bookkeeping and is
 * clamped by `localWrite`; this is the app-level answer that spans both.
 */
/* Old name kept deliberately — see the note on `STORE_KEY` in deck.js. A
 * storage key is an address, not a label, and renaming it silently orphans
 * whatever is already there. */
const ACTIVE_KEY = 'optcg-deck-lab:active';

export function activeDeckId() {
  try {
    return localStorage.getItem(ACTIVE_KEY) || null;
  } catch { return null; }
}

export function setActiveDeckId(id) {
  try {
    if (id == null) localStorage.removeItem(ACTIVE_KEY);
    else localStorage.setItem(ACTIVE_KEY, String(id));
  } catch { /* private mode — the app still works, it just won't remember */ }
}

/* ---------------------------------------------------------------------- */
/* local backend — signed out, and the fallback for every failure          */
/* ---------------------------------------------------------------------- */

/* `loadDecks`/`saveDecks` have always taken an array and an active index; main
 * .js simply never used more than index 0. So this is a migration of *where*
 * the array lives and *how many* entries the app allows, not a new shape. */

const localList = () => {
  const { decks } = loadDecks();
  return decks.map((d, i) => ({ ...emptyDeck(), ...d, id: `local:${i}` }));
};

const localIndex = id => {
  const m = /^local:(\d+)$/.exec(String(id ?? ''));
  return m ? Number(m[1]) : -1;
};

function localWrite(mutate) {
  const { decks, activeId } = loadDecks();
  const next = mutate(decks.slice());
  if (next === null) return fail('That deck is no longer here.');
  const active = Math.min(activeId ?? 0, Math.max(0, next.length - 1));
  saveDecks(next, next.length ? active : null);
  return ok({ decks: next.map((d, i) => ({ ...d, id: `local:${i}` })) });
}

/* ---------------------------------------------------------------------- */
/* remote backend                                                          */
/* ---------------------------------------------------------------------- */

/**
 * Which backend a call will use.
 *
 * Read per call rather than cached: signing in or out mid-session has to take
 * effect on the next save, not on the next reload.
 */
export const isRemote = () => isSignedIn();

/**
 * Turn a supabase-js error into something a person can read.
 *
 * The raw messages are Postgres constraint names — `decks_name_length`,
 * `is_deck_cards` — which name our schema rather than the user's mistake.
 */
export function readable(error) {
  const msg = String(error?.message || error || 'Unknown error');
  if (/decks_name_length/.test(msg)) return `A deck name can be at most ${NAME_MAX} characters.`;
  if (/is_deck_cards|decks_cards_shape/.test(msg)) return 'That card list cannot be stored.';
  if (/decks_leader_format/.test(msg)) return 'That leader is not a card id.';
  if (/row-level security|violates row-level/i.test(msg)) return 'That deck belongs to someone else.';
  if (/JWT|not authenticated/i.test(msg)) return 'Your session expired — sign in again.';
  return msg;
}

/* ---------------------------------------------------------------------- */
/* the API                                                                 */
/* ---------------------------------------------------------------------- */

/**
 * Every deck the current user can see, newest edit first.
 *
 * The ordering matches the `decks_user_id_updated_at_idx` index, which exists
 * for exactly this query.
 */
export async function listDecks() {
  if (!isRemote()) return ok({ decks: localList(), remote: false });
  try {
    const { data, error } = await supabase()
      .from('decks').select(COLUMNS).order('updated_at', { ascending: false });
    if (error) return fail(readable(error));
    return ok({ decks: (data || []).map(toDeck), remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/** Create a deck. Returns the stored deck, including the id the store chose. */
export async function createDeck(deck = emptyDeck()) {
  const problems = storeProblems(deck);
  if (problems.length) return fail(problems[0]);

  if (!isRemote()) {
    const r = localWrite(decks => [...decks, { ...emptyDeck(), ...toRow(deck) }]);
    return r.ok ? ok({ deck: r.decks[r.decks.length - 1], remote: false }) : r;
  }
  try {
    /* `user_id` is set explicitly rather than left to a column default: the
     * RLS insert policy checks it against `auth.uid()`, and a null would be
     * refused by the policy rather than defaulted by the table. */
    const { data, error } = await supabase()
      .from('decks').insert({ ...toRow(deck), user_id: currentUser()?.id })
      .select(COLUMNS).single();
    if (error) return fail(readable(error));
    return ok({ deck: toDeck(data), remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/** Overwrite a deck in place. */
export async function updateDeck(id, deck) {
  const problems = storeProblems(deck);
  if (problems.length) return fail(problems[0]);

  if (!isRemote()) {
    const i = localIndex(id);
    const r = localWrite(decks => (decks[i] ? (decks[i] = { ...decks[i], ...toRow(deck) }, decks) : null));
    return r.ok ? ok({ deck: r.decks[i], remote: false }) : r;
  }
  try {
    const { data, error } = await supabase()
      .from('decks').update(toRow(deck)).eq('id', id).select(COLUMNS).single();
    if (error) return fail(readable(error));
    return ok({ deck: toDeck(data), remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/** Rename without touching the list — the common edit, and the cheapest. */
export async function renameDeck(id, name) {
  const clean = String(name ?? '').trim();
  if (clean.length > NAME_MAX) return fail(`A deck name can be at most ${NAME_MAX} characters.`);

  if (!isRemote()) {
    const i = localIndex(id);
    const r = localWrite(decks => (decks[i] ? (decks[i] = { ...decks[i], name: clean }, decks) : null));
    return r.ok ? ok({ deck: r.decks[i], remote: false }) : r;
  }
  try {
    const { data, error } = await supabase()
      .from('decks').update({ name: clean }).eq('id', id).select(COLUMNS).single();
    if (error) return fail(readable(error));
    return ok({ deck: toDeck(data), remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/**
 * Copy a deck.
 *
 * Reads through this module rather than trusting a deck object handed in by a
 * caller, so duplicating always copies what is actually stored — the bug where
 * "duplicate" silently forks a stale in-memory copy is the whole reason this
 * is a function and not two lines at the call site.
 */
export async function duplicateDeck(id, { suffix = ' copy' } = {}) {
  const found = await getDeck(id);
  if (!found.ok) return found;
  const src = found.deck;
  const name = `${src.name || 'Untitled deck'}${suffix}`.slice(0, NAME_MAX);
  return createDeck({ name, leader: src.leader, cards: { ...src.cards } });
}

/** One deck by id. */
export async function getDeck(id) {
  if (!isRemote()) {
    const deck = localList()[localIndex(id)];
    return deck ? ok({ deck, remote: false }) : fail('That deck is no longer here.');
  }
  try {
    const { data, error } = await supabase()
      .from('decks').select(COLUMNS).eq('id', id).single();
    if (error) return fail(readable(error));
    return ok({ deck: toDeck(data), remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/** Delete a deck. Deleting one that is already gone is a success, not an error. */
export async function deleteDeck(id) {
  /* Forget it as the active deck first, so a failure below cannot leave the
   * app pointing at a row it just tried to remove. */
  if (activeDeckId() === String(id)) setActiveDeckId(null);

  if (!isRemote()) {
    const i = localIndex(id);
    const r = localWrite(decks => (decks[i] ? (decks.splice(i, 1), decks) : null));
    return r.ok ? ok({ remote: false }) : r;
  }
  try {
    const { error } = await supabase().from('decks').delete().eq('id', id);
    if (error) return fail(readable(error));
    return ok({ remote: true });
  } catch (err) {
    return fail(readable(err));
  }
}

/**
 * The deck the app should open, and the list to offer alongside it.
 *
 * One call because the two questions are always asked together and the answer
 * to the first depends on the second: a remembered id that no longer exists —
 * deleted on another device, or a `local:N` left over from before signing in —
 * has to fall back rather than open nothing.
 */
export async function openActive() {
  const listed = await listDecks();
  if (!listed.ok) return listed;

  const { decks } = listed;
  const remembered = activeDeckId();
  const found = decks.find(d => String(d.id) === String(remembered)) ?? decks[0] ?? null;

  // Re-stamp: the remembered id may have been stale, and silently correcting
  // it here is what stops the fallback happening again on every reload.
  setActiveDeckId(found?.id ?? null);
  return ok({ deck: found, decks, remote: listed.remote });
}

/**
 * Offer the browser's deck into the account on first sign-in.
 *
 * Copies rather than moves, and only when the account has no decks at all.
 * Both halves matter:
 *
 *   - **Copy.** The local deck stays where it is, so signing out does not take
 *     your work with it, and a failed upload costs nothing.
 *   - **Only into an empty account.** Signing in on a second machine must not
 *     push that machine's leftover local deck into an account that already has
 *     twelve. This runs once, for the person who had a deck before they had an
 *     account, which is everyone who used the app before #47.
 *
 * Returns `{ ok, migrated, opened }` — `migrated: 0` is the normal,
 * uninteresting answer and is not a failure.
 *
 * `opened` is the account copy of whichever deck was open in this browser when
 * they signed in, and it exists because of #109: boot calls `openActive()`
 * before the auth event has arrived, finds an account with no decks in it, and
 * falls back to an empty one. Nothing then re-opens anything, so the header
 * reads "Untitled deck · 0/50" while the deck sits safely in My Decks — which
 * looks exactly like signing in threw your work away, at the moment the user
 * has least reason to trust the account feature. The caller needs to know
 * *which* deck to put back, and only this function is in a position to say.
 */
export async function migrateLocalDeck() {
  if (!isRemote()) return ok({ migrated: 0, opened: null, reason: 'signed out' });

  const existing = await listDecks();
  if (!existing.ok) return existing;
  if (existing.decks.length) return ok({ migrated: 0, opened: null, reason: 'account already has decks' });

  /* An empty deck is not worth an account row — everyone who opened the app
   * once has one, and migrating it would greet you with "Untitled deck". */
  const local = localList().filter(d => d.leader || Object.keys(d.cards).length);
  if (!local.length) return ok({ migrated: 0, opened: null, reason: 'nothing worth keeping' });

  /* The local array's own bookkeeping, not `activeDeckId()`. They answer the
   * same question, but boot's `openActive()` has already re-stamped the
   * app-level one to null on the way past — that is the same ordering that
   * causes the bug. `localList` assigns `local:N` before the filter above, so
   * these ids still line up with the stored array. */
  const wasOpen = `local:${loadDecks().activeId ?? 0}`;

  let migrated = 0;
  let opened = null;
  for (const deck of local) {
    const r = await createDeck({ ...deck, name: deck.name || 'My deck' });
    if (!r.ok) return { ...r, migrated, opened };
    if (deck.id === wasOpen || !opened) opened = r.deck;
    migrated++;
  }
  /* `|| !opened` above means the first migrated deck stands in when the
   * remembered index does not name one — it was empty and filtered out, or the
   * bookkeeping was lost. Any real deck beats "Untitled deck". */
  return ok({ migrated, opened });
}
